import { useState } from "react";

import type * as types from "./types";

import {
  type ApolloError,
  type DocumentNode,
  type MutationResult,
  type OperationVariables,
  type TypedDocumentNode,
  useMutation,
} from "@apollo/client";
import Bugsnag from "@bugsnag/browser";

import { useClient } from "./use-client";

type MutationPromiseTuple<T, V> = [
  (variables: V) => Promise<T>,
  MutationResult<T>,
];
// This is a wrapper around useMutation that wraps the mutation function in a promise
export function useNorthbeamMutationPromise<T, V = OperationVariables>(
  mutation: DocumentNode | TypedDocumentNode<T, V>,
  options?: types.MutationOptions<T, V>,
): MutationPromiseTuple<T, V> {
  const [promise, setPromise] = useState<{
    resolve: (data: T) => void;
    reject: (error: ApolloError) => void;
  }>({
    resolve: () => {
      throw new Error(
        "The promise was not properly set in state before the API request was made!",
      );
    },
    reject: () => {
      throw new Error(
        "The promise was not properly set in state before the API request was made!",
      );
    },
  });

  const client = useClient(options);

  const [caller, result] = useMutation<T, V>(mutation, {
    ...options,
    client,
    onCompleted: (data: T) => promise.resolve(data),
    onError: (error: ApolloError) => promise.reject(error),
  });

  const callerPromise = (variables: V) =>
    new Promise<T>((resolve, reject) => {
      setPromise({ resolve, reject });
      caller({ variables });
    });

  if (result.error && !options?.doNotRaiseErrorOnFailure) {
    Bugsnag.setContext(
      `GraphQL mutation failure. \nMutation: ${JSON.stringify(
        mutation,
      )}\nOptions: ${JSON.stringify(options)}`,
    );
    throw result.error;
  }

  return [callerPromise, result];
}
